/* eslint-disable object-curly-newline */
import React, { useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import PageHeading from '../../../common/components/PageHeading/PageHeading';
import ContactInfo from '../../../common/ContactInfo/ContactInfo';
import {
  useFormControls,
  Validator,
  isRequired,
  validateEmailFormat,
} from '../../../common/hooks/useFormControls';
import styles from './ContactScreen.module.scss';
import EventListingsPriceBox from '../components/EventListingsPriceBox';
import { eventListingsURL } from '../constants';
import { scrollToTopOrError } from '../../../common/helpers/scrollTo';
import displayBackendErrors from '../../../common/helpers/displayBackendErrors';

import EventListingService from '../services/EventListingService';

const ContactScreen = () => {
  const { site, status, eventListingContactEmail } = window.eventListings;
  const { token } = useParams();
  const { globalState: state, setGlobalState, pushEvent } = useOutletContext();
  const api = new EventListingService();
  const navigate = useNavigate();

  // FORM VALIDATORS //
  const requiredField = Validator(isRequired, 'THIS FIELD IS REQUIRED.');
  const validEmail = Validator(validateEmailFormat, 'ENTER A VALID EMAIL.');

  // FORM FIELDS //
  const formControls = [
    { label: 'Full name', fieldName: 'submitterName', value: '', validators: [requiredField] },
    { label: 'Email address',
      description: 'Enter a professional email address associated with your business domain.',
      fieldName: 'submitterEmail',
      value: '',
      validators: [requiredField, validEmail] },
    { label: 'Job title', fieldName: 'submitterTitle', value: '' },
    { label: 'Company name', fieldName: 'submitterCompany', value: '', validators: [requiredField] },
  ];
  const {
    fields,
    setField,
    getErroringFieldNames,
    validateField,
    getFormValues,
    updateForm,
    setError,
  } = useFormControls(formControls);

  async function handleSubmit() {
    const erroringFieldNames = getErroringFieldNames();
    if (erroringFieldNames.length === 0) {
      const formValues = getFormValues();
      try {
        const responseData = token
          ? await api.updateEventListing(token, formValues)
          : await api.createEventListingPurchase(formValues);
        setGlobalState(responseData);
        // send GTM event
        pushEvent('contactScreenSubmit', { event_listing_purchase_id: responseData.eventListingPurchase.purchaseId });

        window.eventListings.status = responseData.eventListingPurchase.status;
        navigate(`${eventListingsURL}/${responseData.eventListingPurchase.purchaseToken}/details`);
      } catch (error) {
        displayBackendErrors(error, setError, null, formControls);
        scrollToTopOrError(Object.keys(error?.response?.data));
      }
    }
    scrollToTopOrError(erroringFieldNames);
  }

  useEffect(() => { if (state) updateForm(state.eventListingPurchase); }, [state]);

  const isPaid = status === 'PAID';
  const isValid = Object.values(fields).every((field) => field.valid);
  const isDirty = Object.values(fields).some((field) => field.dirty);
  const isSubmittable = !isPaid || (isValid && isDirty);

  const pageDescription = status === 'PAID'
    ? (
      <>
        Make changes to a published event. Reach out to{' '}
        <a href={`emailto:${eventListingContactEmail}`}>{eventListingContactEmail}</a>
        {' '}for billing questions.
      </>
    )
    : (
      <>
        <span className={styles.pageDescription}>
          {/* the space at the end of the below string is load-bearing */}
          {`Promote your event on ${site.name} to ${site.unique_monthly_visitors} industry professionals. `}
        </span>
        <span className={styles.pageDescription}>
          Our platform supports both in-person and virtual events.
        </span>
      </>
    );

  return (
    <>
      <PageHeading
        title={status === 'PAID' ? 'Manage Event Contact' : 'Promote an Event'}
        description={pageDescription}
      />
      <div className="row">
        <div className="medium-4 medium-push-8 column">
          <EventListingsPriceBox site={site} />
        </div>
        <div className="medium-8 medium-pull-4 column">
          <ContactInfo
            title="Contact Information"
            description="This information will be kept private. It will not be displayed with the event promotion."
            name={fields.submitterName}
            email={fields.submitterEmail}
            jobTitle={fields.submitterTitle}
            company={fields.submitterCompany}
            onChange={setField}
            onBlur={validateField}
          />
          <div className={styles.buttonWrapper}>
            <button
              type="button"
              name="submitCreate"
              className={`button button--${isSubmittable ? 'medium' : 'disabled'}`}
              disabled={!isSubmittable}
              onClick={handleSubmit}
            >
              {status === 'PAID' ? 'Update' : 'Get started'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactScreen;
